import {
  AppstoreAddOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Alert, Button, Form, Input, Modal } from 'antd';
import { useEffect, useState } from 'react';

import { computeExperienceName } from 'app/components/commons/Experience/utils';
import { EmptyState } from 'app/components/pages/Extranet/commons/EmptyState/EmptyState';
import { RedirectIcon } from 'app/components/pages/Hotels/RedirectIcon';
import { useGetExperiencesByHotelId } from 'app/hooks/data/core/admin/hotels/useHotelExperiences';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { EXPERIENCE_STATUS } from 'app/typings/core/admin/experiences/enum/experienceStatus';
import type { ExperienceResponse } from 'app/typings/core/admin/experiences/experience';
import type {
  PackExperienceResponse,
  PackResponse,
} from 'app/typings/core/admin/packs/pack';

import './AddExperiencesModal.scss';
import { ExperienceListItem } from './ExperienceListItem';

type AddExperiencesModalProps = {
  visible: boolean;
  onClose: () => void;
};

export const AddExperiencesModal = ({
  visible,
  onClose,
}: AddExperiencesModalProps) => {
  const form = Form.useFormInstance<
    Partial<PackResponse> & {
      packExperiences: PackExperienceResponse[];
    }
  >();
  const formValues = Form.useWatch([], form) ?? {};

  const {
    data: hotelExperiences,
    refetch: refetchHotelExperiences,
    isFetching: isFetchingHotelExperiences,
  } = useGetExperiencesByHotelId(formValues.hotelId, {
    withStaycationExperiences: true,
  });

  const { data: experienceCategories } = useExperiencesCategories();
  const [selectedExperienceIds, setSelectedExperienceIds] = useState<
    Set<number>
  >(new Set());

  const [isOpened, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (visible) {
      setSearchTerm('');
    }
  }, [visible]);

  useEffect(() => {
    setSelectedExperienceIds(
      new Set((formValues.packExperiences || []).map((exp) => exp.experienceId))
    );
  }, [formValues.packExperiences]);

  const handleCheckboxChange = (experienceId: number, checked: boolean) => {
    setSelectedExperienceIds((prev) => {
      const copy = new Set(prev);

      if (checked) {
        copy.add(experienceId);
      } else {
        copy.delete(experienceId);
      }

      return copy;
    });
  };

  const handleSubmit = () => {
    if (!hotelExperiences) {
      return;
    }

    const allSelectedIds = selectedExperienceIds;
    const existingPackExperiences = formValues.packExperiences || [];

    let maxIncludedPriority = existingPackExperiences
      .filter((e) => e.isIncluded)
      .reduce((max, e) => Math.max(max, e.priority ?? 0), -1);

    const newPackExperiences: PackExperienceResponse[] =
      hotelExperiences.experiences
        .filter((exp) => allSelectedIds.has(exp.id))
        .map((exp) => {
          const existing = existingPackExperiences.find(
            (pkg) => pkg.experienceId === exp.id
          );

          if (existing) {
            return existing;
          }

          const priority = ++maxIncludedPriority;

          return {
            experienceId: exp.id,
            priority,
            isOptional: false,
            isPerPerson: false,
            quantity: 1,
            isIncluded: true,
            discount: undefined,
            discover: false,
            pictures: [],
          };
        });

    form.setFieldValue('packExperiences', newPackExperiences);
    form.setFields([{ name: 'packExperiences', touched: true }]);
    form.validateFields();

    onClose();
  };

  const getExperienceName = (exp: ExperienceResponse) =>
    computeExperienceName({
      category: experienceCategories?.find((cat) => cat.id === exp.categoryId),
      quantity: null,
      brand: exp.brand ?? null,
      duration: exp.duration ?? null,
      durationDescription: exp.durationDescription ?? null,
      time: exp.time ?? null,
      locations: exp.location ?? null,
    });

  const normalizedSearch = searchTerm.trim().toLowerCase();

  const matchesSearch = (exp: ExperienceResponse) => {
    if (!normalizedSearch) {
      return true;
    }

    const categoryName =
      experienceCategories?.find((cat) => cat.id === exp.categoryId)?.name ??
      '';

    return `${getExperienceName(exp)} ${categoryName}`
      .toLowerCase()
      .includes(normalizedSearch);
  };

  const sortExperiences = (experiences: ExperienceResponse[]) =>
    [...experiences].sort((a, b) => {
      const aIsPublished = a.status === EXPERIENCE_STATUS.PUBLISHED;
      const bIsPublished = b.status === EXPERIENCE_STATUS.PUBLISHED;

      if (aIsPublished !== bIsPublished) {
        return aIsPublished ? -1 : 1;
      }

      const nameA = getExperienceName(a);
      const nameB = getExperienceName(b);

      return nameA.localeCompare(nameB);
    });

  const isDaytime = formValues.isDaytime;

  const filteredExperiences = hotelExperiences?.experiences
    .filter((exp) =>
      isDaytime
        ? !!exp.hotelId
        : !exp.isAlwaysIncludedWithNight && !!exp.hotelId
    )
    .filter(matchesSearch);

  const originalExperiences = hotelExperiences?.experiences
    .filter((exp) => !exp.isAlwaysIncludedWithNight && !exp.hotelId)
    .filter(matchesSearch);

  const renderExperienceList = (
    isOriginal: boolean,
    experiences?: ExperienceResponse[]
  ) => {
    return experiences && experiences?.length > 0 ? (
      sortExperiences(experiences)?.map((exp) => (
        <ExperienceListItem
          key={exp.id}
          experience={exp}
          selected={selectedExperienceIds.has(exp.id)}
          onToggle={handleCheckboxChange}
          categoryName={
            experienceCategories?.find((cat) => cat.id === exp.categoryId)?.name
          }
        />
      ))
    ) : (
      <EmptyState
        title="No activities"
        description={`No ${
          isOriginal ? 'Staycation ' : 'hotel '
        }activities configured`}
        icon={<AppstoreAddOutlined />}
      />
    );
  };

  const isStaycationListOpened = isOpened || !!normalizedSearch;

  const hasSearch = !!normalizedSearch;
  const hotelHasResults = (filteredExperiences?.length ?? 0) > 0;
  const staycationHasResults = (originalExperiences?.length ?? 0) > 0;
  const hasResults = hotelHasResults || staycationHasResults;

  const createActivityUrl = formValues.hotelId
    ? `/activities/new?hotelId=${formValues.hotelId}`
    : undefined;

  return (
    <Modal
      title="Select Activities"
      open={visible}
      onCancel={onClose}
      centered
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSubmit}>
          Save
        </Button>,
      ]}
      width={800}
      className="add-experiences-modal"
    >
      <div className="search-bar">
        <Input
          placeholder="Search an activity..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          addonAfter={<SearchOutlined />}
          allowClear
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refetchHotelExperiences()}
          loading={isFetchingHotelExperiences}
        >
          Refresh list
        </Button>
      </div>

      <div className="modal-scroll-area">
        {(!hasSearch || hotelHasResults) && (
          <div className="activity-section">
            <h3>Hotel activities</h3>
            <div className="experience-list">
              {renderExperienceList(false, filteredExperiences)}
            </div>
          </div>
        )}

        {(!hasSearch || staycationHasResults) && (
          <div className="activity-section">
            <div
              className="collapsible-header"
              onClick={() => setOpen(!isOpened)}
            >
              <span className={`arrow ${isStaycationListOpened ? 'open' : ''}`}>
                <RightOutlined />
              </span>
              <h3>Staycation activities</h3>
            </div>

            {isStaycationListOpened && (
              <div className="experience-list">
                {renderExperienceList(true, originalExperiences)}
              </div>
            )}
          </div>
        )}

        {hasSearch && (
          <Alert
            className="create-activity-alert"
            type="info"
            message={
              hasResults
                ? "Didn't find the right activity?"
                : 'No activity found'
            }
            description={
              hasResults
                ? 'Create one in a new tab, then refresh.'
                : "We couldn't find any matching results."
            }
            action={
              <Button
                href={createActivityUrl}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!createActivityUrl}
              >
                Create new activity
                <RedirectIcon />
              </Button>
            }
          />
        )}
      </div>
    </Modal>
  );
};
